import React, { useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { FileText, Mail, Phone, Globe, Scale, BookOpen, Copyright, AlertTriangle, Shield, ExternalLink } from 'lucide-react';

const ImpressumPage = () => {
  const [language, setLanguage] = useState('de');

  const content = {
    de: {
      title: "Impressum",
      subtitle: "Angaben gemäß § 5 DDG",
      lastUpdated: "Stand: Mai 2026",
      sections: {
        provider: {
          title: "Diensteanbieter",
          description: "Angaben gemäß § 5 Digitale-Dienste-Gesetz (DDG)"
        },
        contact: {
          title: "Kontakt",
          description: "Erreichbarkeit"
        },
        content: {
          title: "Verantwortlich für den Inhalt",
          subtitle: "gemäß § 18 Abs. 2 MStV",
          description: "Inhaltlich Verantwortlicher im Sinne des Medienstaatsvertrags:",
          note: "Der Verantwortliche entscheidet allein oder gemeinsam mit anderen über die Zwecke und Mittel der Verarbeitung von personenbezogenen Daten."
        },
        purpose: {
          title: "Art des Unternehmens",
          type: "Einzelunternehmen",
          activity: "Geschäftstätigkeit: Bereitstellung einer kostenlosen Online-Lernplattform für die deutsche Sprache (E-Learning).",
          note: ""
        },
        copyright: {
          title: "Urheberrecht",
          text: "Die durch den Seitenbetreiber erstellten Inhalte und Werke auf diesen Seiten unterliegen dem deutschen Urheberrecht. Die Vervielfältigung, Bearbeitung, Verbreitung und jede Art der Verwertung außerhalb der Grenzen des Urheberrechts bedürfen der schriftlichen Zustimmung des jeweiligen Autors bzw. Erstellers. Downloads und Kopien dieser Seite sind nur für den privaten, nicht kommerziellen Gebrauch gestattet.",
          note: "Soweit die Inhalte auf dieser Seite nicht vom Betreiber erstellt wurden, werden die Urheberrechte Dritter beachtet. Insbesondere werden Inhalte Dritter als solche gekennzeichnet. Sollten Sie trotzdem auf eine Urheberrechtsverletzung aufmerksam werden, bitten wir um einen entsprechenden Hinweis. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Inhalte umgehend entfernen."
        },
        disclaimer: {
          title: "Haftungsausschluss",
          liability: {
            title: "Haftung für Inhalte",
            text: "Als Diensteanbieter sind wir gemäß § 7 Abs. 1 DDG für eigene Inhalte auf diesen Seiten nach den allgemeinen Gesetzen verantwortlich. Nach §§ 8 bis 10 DDG sind wir als Diensteanbieter jedoch nicht verpflichtet, übermittelte oder gespeicherte fremde Informationen zu überwachen oder nach Umständen zu forschen, die auf eine rechtswidrige Tätigkeit hinweisen. Verpflichtungen zur Entfernung oder Sperrung der Nutzung von Informationen nach den allgemeinen Gesetzen bleiben hiervon unberührt. Eine diesbezügliche Haftung ist jedoch erst ab dem Zeitpunkt der Kenntnis einer konkreten Rechtsverletzung möglich. Bei Bekanntwerden von entsprechenden Rechtsverletzungen werden wir diese Inhalte umgehend entfernen."
          },
          links: {
            title: "Haftung für Links",
            text: "Unser Angebot enthält Links zu externen Webseiten Dritter, auf deren Inhalte wir keinen Einfluss haben. Deshalb können wir für diese fremden Inhalte auch keine Gewähr übernehmen. Für die Inhalte der verlinkten Seiten ist stets der jeweilige Anbieter oder Betreiber der Seiten verantwortlich. Die verlinkten Seiten wurden zum Zeitpunkt der Verlinkung auf mögliche Rechtsverstöße überprüft. Rechtswidrige Inhalte waren zum Zeitpunkt der Verlinkung nicht erkennbar. Eine permanente inhaltliche Kontrolle der verlinkten Seiten ist jedoch ohne konkrete Anhaltspunkte einer Rechtsverletzung nicht zumutbar. Bei Bekanntwerden von Rechtsverletzungen werden wir derartige Links umgehend entfernen."
          }
        },
        dataProtection: {
          title: "Datenschutz",
          text: "Die Nutzung unserer Website ist in der Regel ohne Angabe personenbezogener Daten möglich. Soweit auf unseren Seiten personenbezogene Daten (beispielsweise Name, Anschrift oder E-Mail-Adressen) erhoben werden, erfolgt dies, soweit möglich, stets auf freiwilliger Basis. Diese Daten werden ohne Ihre ausdrückliche Zustimmung nicht an Dritte weitergegeben.",
          link: "Ausführliche Informationen zum Datenschutz finden Sie in unserer",
          linkText: "Datenschutzerklärung"
        },
        dispute: {
          title: "Streitschlichtung",
          text: "Die Europäische Kommission stellt eine Plattform zur Online-Streitbeilegung (OS) bereit:",
          linkText: "https://ec.europa.eu/consumers/odr/",
          note: "Wir sind nicht bereit oder verpflichtet, an Streitbeilegungsverfahren vor einer Verbraucherschlichtungsstelle teilzunehmen."
        },
        legal: {
          title: "Rechtliche Hinweise",
          items: [
            "Alle Texte, Bilder und weitere hier veröffentlichten Informationen unterliegen dem Urheberrecht des Anbieters, soweit keine Urheberrechte Dritter bestehen.",
            "Die Lernmaterialien dienen ausschließlich dem persönlichen, nicht-kommerziellen Gebrauch.",
            "Trotz sorgfältiger Kontrolle übernehmen wir keine Haftung für die Richtigkeit und Vollständigkeit der Lerninhalte.",
            "Die deutsche Sprache wird von qualifizierten Inhalten vermittelt, wobei wir keine offizielle Sprachzertifizierung anbieten."
          ]
        }
      }
    },
    en: {
      title: "Legal Notice",
      subtitle: "Information pursuant to § 5 DDG",
      lastUpdated: "Last Updated: May 2026",
      sections: {
        provider: {
          title: "Service Provider",
          description: "Information in accordance with § 5 Digital Services Act (DDG)"
        },
        contact: {
          title: "Contact",
          description: "How to reach us"
        },
        content: {
          title: "Responsible for Content",
          subtitle: "pursuant to § 18 para. 2 MStV",
          description: "Person responsible for content in accordance with the Interstate Media Treaty:",
          note: "The person responsible decides alone or together with others on the purposes and means of processing personal data."
        },
        purpose: {
          title: "Type of Business",
          type: "Sole Proprietorship",
          activity: "Business Activity: Providing a free online learning platform for the German language (E-Learning).",
          note: ""
        },
        copyright: {
          title: "Copyright",
          text: "The content and works created by the site operator on these pages are subject to German copyright law. Reproduction, editing, distribution, and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator. Downloads and copies of this page are only permitted for private, non-commercial use.",
          note: "Insofar as the content on this page was not created by the operator, the copyrights of third parties are respected. In particular, third-party content is marked as such. Should you become aware of a copyright infringement, please notify us accordingly. Upon becoming aware of legal violations, we will remove such content immediately."
        },
        disclaimer: {
          title: "Disclaimer",
          liability: {
            title: "Liability for Content",
            text: "As a service provider, we are responsible for our own content on these pages in accordance with § 7 para. 1 DDG under general law. According to §§ 8 to 10 DDG, however, we as a service provider are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity. Obligations to remove or block the use of information under general law remain unaffected. However, liability in this regard is only possible from the time of knowledge of a specific legal violation. Upon becoming aware of corresponding legal violations, we will remove this content immediately."
          },
          links: {
            title: "Liability for Links",
            text: "Our offer contains links to external third-party websites, over whose content we have no influence. Therefore, we cannot assume any liability for these external contents. The respective provider or operator of the pages is always responsible for the content of the linked pages. The linked pages were checked for possible legal violations at the time of linking. Illegal content was not recognizable at the time of linking. However, permanent content control of the linked pages is not reasonable without concrete evidence of a legal violation. Upon becoming aware of legal violations, we will remove such links immediately."
          }
        },
        dataProtection: {
          title: "Data Protection",
          text: "The use of our website is generally possible without providing personal data. As far as personal data (e.g., name, address, or email addresses) is collected on our pages, this is always done on a voluntary basis as far as possible. This data will not be passed on to third parties without your express consent.",
          link: "Detailed information on data protection can be found in our",
          linkText: "Privacy Policy"
        },
        dispute: {
          title: "Dispute Resolution",
          text: "The European Commission provides a platform for online dispute resolution (ODR):",
          linkText: "https://ec.europa.eu/consumers/odr/",
          note: "We are not willing or obliged to participate in dispute resolution proceedings before a consumer arbitration board."
        },
        legal: {
          title: "Legal Information",
          items: [
            "All texts, images, and other information published here are subject to the copyright of the provider, unless third-party copyrights exist.",
            "The learning materials are intended exclusively for personal, non-commercial use.",
            "Despite careful control, we assume no liability for the correctness and completeness of the learning content.",
            "The German language is taught through qualified content, although we do not offer official language certification."
          ]
        }
      }
    }
  };

  const t = content[language].sections;

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-babyblue-50 via-babyblue-100/50 to-white">
      <Navbar />

      <main className="flex-1 py-12 px-6">
        <div className="container mx-auto max-w-4xl">
          {/* Header with Language Toggle */}
          <div className="text-center mb-8">
            <FileText className="w-16 h-16 text-primary mx-auto mb-4" />
            <h1 className="font-heading text-4xl font-bold text-primary mb-2">
              Impressum <span className="text-babyblue-500 font-normal">/ Legal Notice</span>
            </h1>
            <p className="text-babyblue-600">{content[language].subtitle}</p>
            <p className="text-babyblue-500 text-sm mt-2">{content[language].lastUpdated}</p>
            
            {/* Language Toggle */}
            <div className="flex items-center justify-center gap-3 mt-6">
              <Globe className="w-5 h-5 text-babyblue-500" />
              <div className="flex bg-babyblue-100 rounded-full p-1">
                <button
                  onClick={() => setLanguage('de')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    language === 'de' 
                      ? 'bg-primary text-white' 
                      : 'text-babyblue-600 hover:text-babyblue-800'
                  }`}
                  data-testid="impressum-lang-de"
                >
                  Deutsch
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${
                    language === 'en' 
                      ? 'bg-primary text-white' 
                      : 'text-babyblue-600 hover:text-babyblue-800'
                  }`}
                  data-testid="impressum-lang-en"
                >
                  English
                </button>
              </div>
            </div>
          </div>
          
          <div className="card p-8 space-y-8 bg-white border border-babyblue-200">
            
            {/* Section: Service Provider */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-2 flex items-center gap-3">
                <BookOpen className="w-6 h-6 text-primary" />
                {t.provider.title}
              </h2>
              <p className="text-babyblue-500 text-sm mb-4">{t.provider.description}</p>
              
              <div className="bg-babyblue-50 border border-babyblue-200 rounded-lg p-5">
                <p className="text-babyblue-700 font-semibold text-xl">Teoman Talay</p>
                <p className="text-primary font-medium mt-1">Germanatic - German Integration Platform</p>
                <div className="mt-4 space-y-1">
                  <p className="text-babyblue-600">Marktstraße 5</p>
                  <p className="text-babyblue-600">72458 Albstadt</p>
                  <p className="text-babyblue-600">Deutschland / Germany</p>
                </div>
              </div>
            </section>

            {/* Section: Contact */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-2 flex items-center gap-3">
                <Mail className="w-6 h-6 text-primary" />
                {t.contact.title}
              </h2>
              <p className="text-babyblue-500 text-sm mb-4">{t.contact.description}</p>
              
              <div className="bg-babyblue-50 p-5 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-babyblue-500 text-xs">E-Mail</p>
                    <a href="mailto:teomantalay21@gmail.com" className="text-babyblue-700 hover:text-primary transition-colors">
                      teomantalay21@gmail.com
                    </a>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-babyblue-500 text-xs">{language === 'de' ? 'Telefon' : 'Phone'}</p>
                    <a href="tel:+4915902221377" className="text-babyblue-700 hover:text-primary transition-colors">
                      +49 1590 2221377
                    </a>
                  </div>
                </div>
              </div>
            </section>

            {/* Section: Responsible for Content */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-2 flex items-center gap-3">
                <Scale className="w-6 h-6 text-primary" />
                {t.content.title}
              </h2>
              <p className="text-primary text-sm mb-2">{t.content.subtitle}</p>
              <p className="text-babyblue-500 text-sm mb-4">{t.content.description}</p>
              
              <div className="bg-babyblue-50 p-4 rounded-lg">
                <p className="text-babyblue-700 font-semibold">Teoman Talay</p>
                <p className="text-babyblue-600 text-sm mt-1">Marktstraße 5, 72458 Albstadt, Germany</p>
              </div>
              <p className="text-babyblue-400 text-xs mt-3 italic">{t.content.note}</p>
            </section>

            {/* Section: Type of Business */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-4">
                {t.purpose.title}
              </h2>
              <div className="bg-babyblue-50 p-5 rounded-lg space-y-3">
                <div className="flex items-center gap-2">
                  <span className="bg-primary/20 text-primary px-3 py-1 rounded text-sm font-medium">
                    {t.purpose.type}
                  </span>
                </div>
                <p className="text-babyblue-600">{t.purpose.activity}</p>
                {t.purpose.note && (
                  <p className="text-babyblue-500 text-sm italic">{t.purpose.note}</p>
                )}
              </div>
            </section>

            {/* Section: Copyright */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-4 flex items-center gap-3">
                <Copyright className="w-6 h-6 text-primary" />
                {t.copyright.title}
              </h2>
              <div className="bg-babyblue-50 p-5 rounded-lg">
                <p className="text-babyblue-600 leading-relaxed">{t.copyright.text}</p>
                <p className="text-babyblue-500 text-sm mt-4 italic">{t.copyright.note}</p>
              </div>
            </section>

            {/* Section: Disclaimer */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-4 flex items-center gap-3">
                <AlertTriangle className="w-6 h-6 text-primary" />
                {t.disclaimer.title}
              </h2>
              
              <div className="space-y-4">
                <div className="bg-babyblue-50 p-5 rounded-lg">
                  <h3 className="font-semibold text-primary mb-3">{t.disclaimer.liability.title}</h3>
                  <p className="text-babyblue-600 text-sm leading-relaxed">{t.disclaimer.liability.text}</p>
                </div>
                
                <div className="bg-babyblue-50 p-5 rounded-lg">
                  <h3 className="font-semibold text-primary mb-3">{t.disclaimer.links.title}</h3>
                  <p className="text-babyblue-600 text-sm leading-relaxed">{t.disclaimer.links.text}</p>
                </div>
              </div>
            </section>

            {/* Section: Data Protection */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-4 flex items-center gap-3">
                <Shield className="w-6 h-6 text-primary" />
                {t.dataProtection.title}
              </h2>
              <div className="bg-babyblue-50 p-5 rounded-lg">
                <p className="text-babyblue-600 leading-relaxed">{t.dataProtection.text}</p>
                <p className="text-babyblue-500 text-sm mt-4">
                  {t.dataProtection.link}{' '}
                  <a href="/datenschutz" className="text-primary hover:underline inline-flex items-center gap-1">
                    {t.dataProtection.linkText}
                    <ExternalLink className="w-3 h-3" />
                  </a>
                </p>
              </div>
            </section>

            {/* Section: Dispute Resolution */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-4">
                {t.dispute.title}
              </h2>
              <div className="bg-babyblue-50 p-5 rounded-lg">
                <p className="text-babyblue-600 mb-3">{t.dispute.text}</p>
                <a 
                  href="https://ec.europa.eu/consumers/odr/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-2"
                >
                  <Globe className="w-4 h-4" />
                  {t.dispute.linkText}
                </a>
                <p className="text-babyblue-500 text-sm mt-4 italic">{t.dispute.note}</p>
              </div>
            </section>

            {/* Section: Legal Information */}
            <section>
              <h2 className="font-heading text-2xl font-semibold text-babyblue-800 mb-4">
                {t.legal.title}
              </h2>
              <div className="bg-babyblue-50 p-5 rounded-lg">
                <ul className="space-y-3">
                  {t.legal.items.map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-babyblue-600 text-sm">
                      <span className="text-primary mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>

            {/* Footer */}
            <div className="border-t border-babyblue-200 pt-6 text-center">
              <p className="text-babyblue-400 text-sm">
                © {new Date().getFullYear()} Germanatic - German Integration Platform. {language === 'de' ? 'Alle Rechte vorbehalten.' : 'All rights reserved.'}
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ImpressumPage;
